// apps/client/src/pages/accounts/Accounts.tsx
import {
  ActivityAllocation,
  AllocationStatus,
  ProjectAccount,
  ResearchActivity,
} from '@avanzu/types';
import {
  AutoFixHigh,
  AutoFixHighOutlined,
  DeleteSweep,
  HelpOutline,
  PlaylistAddCheck,
  PlaylistRemove,
} from '@mui/icons-material';
import { IconButton, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import React from 'react';
import CustomPercentageInput from './CustomPercentageInput';

const AllocationStatusIcon: React.FC<{ status: AllocationStatus }> = React.memo(({ status }) => {
  switch (status) {
    case AllocationStatus.PENDING:
      return (
        <Tooltip title="This account has no rule">
          <AutoFixHighOutlined color="disabled" fontSize="small" />
        </Tooltip>
      );
    case AllocationStatus.ALLOCATED:
      return (
        <Tooltip title="This account has an allocation rule">
          <AutoFixHigh color="success" fontSize="small" />
        </Tooltip>
      );
    case AllocationStatus.EXCLUDED:
      return (
        <Tooltip title="This account is excluded from R&D allocations">
          <PlaylistRemove color="error" fontSize="small" />
        </Tooltip>
      );
    default:
      return null;
  }
});

/**
 * -----------------------
 * AccountRow (memoized)
 * -----------------------
 *
 * - Keeps local allocation state so typing only re-renders this row.
 * - Calls parent save/exclude/delete handlers only when user triggers them.
 * - Mirrors all logic & disabled states from original file.
 */
type AccountAllocationRowProps = {
  account: ProjectAccount;
  sortedActivities: ResearchActivity[];
  initialAllocations: Record<string, number>;
  saving: boolean;
  onCreateRule: (allocations: ActivityAllocation[]) => void;
  onExcludeAccount: () => void;
  onDeleteRule: () => void;
};

const MemoCustomPercentageInput = React.memo(CustomPercentageInput);

const AccountAllocationRow: React.FC<AccountAllocationRowProps> = React.memo(
  ({
    account,
    sortedActivities,
    initialAllocations,
    saving,
    onCreateRule,
    onExcludeAccount,
    onDeleteRule,
  }) => {
    // Local allocations for this row (edits are local until saved)
    const [allocations, setAllocations] = React.useState<Record<string, number>>(
      initialAllocations || {}
    );

    // Keep local state in sync if parent updates (e.g. after save / sync)
    React.useEffect(() => {
      setAllocations(initialAllocations || {});
    }, [initialAllocations]);

    const handleLocalAllocationChange = (activityId: string, percentage: number) => {
      setAllocations((prev) => ({
        ...prev,
        [activityId]: percentage,
      }));
    };

    const totalAllocated = Object.values(allocations).reduce((sum, val) => sum + val, 0);
    const isComplete = totalAllocated === 100;
    const isSaving = saving;

    let allocationStatus: AllocationStatus;
    if (account.isExcluded) {
      allocationStatus = AllocationStatus.EXCLUDED;
    } else if (account.allocations && account.allocations.length > 0) {
      allocationStatus = AllocationStatus.ALLOCATED;
    } else {
      allocationStatus = AllocationStatus.PENDING;
    }

    const isAllocateDisabled = totalAllocated <= 0 || totalAllocated > 100 || isSaving;
    const isExcludeDisabled = account.isExcluded || isSaving || isComplete;
    const isAllocatedOrExcluded =
      (account.allocations && account.allocations.length > 0) || account.isExcluded;
    const isDeleteDisabled = !isAllocatedOrExcluded || isSaving;

    const handleCreateRuleClick = () => {
      const allocationsList: ActivityAllocation[] = Object.entries(allocations)
        .filter(([_, percentage]) => percentage > 0)
        .map(([activityId, percentage]) => ({
          activityId,
          allocation: percentage,
        }));
      onCreateRule(allocationsList);
    };

    return (
      <TableRow key={account.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
        <TableCell sx={{ width: '450px', minWidth: '450px' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <AllocationStatusIcon status={allocationStatus} />
            <Stack>
              <Typography variant="body2" fontWeight={600}>
                {account.code} - {account.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {account.description}
              </Typography>
            </Stack>
          </Stack>
        </TableCell>

        <TableCell
          sx={{
            width: '120px',
            minWidth: '120px',
            textAlign: 'center',
            verticalAlign: 'middle',
            padding: 1,
          }}
        >
          <Stack direction="row" justifyContent="center" alignItems="center" spacing={0}>
            <Tooltip title="Remove rule" arrow>
              <span>
                <IconButton
                  color="error"
                  disabled={isDeleteDisabled}
                  size="small"
                  onClick={onDeleteRule}
                >
                  <DeleteSweep fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Exclude account from allocations" arrow>
              <span>
                <IconButton
                  color={account.isExcluded ? 'success' : 'primary'}
                  disabled={isExcludeDisabled && !account.isExcluded}
                  size="small"
                  onClick={onExcludeAccount}
                >
                  <PlaylistRemove fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Save allocation rule" arrow>
              <span>
                <IconButton
                  size="small"
                  color={
                    account.allocations && account.allocations.length > 0 ? 'success' : 'primary'
                  }
                  disabled={isAllocateDisabled}
                  onClick={handleCreateRuleClick}
                >
                  <PlaylistAddCheck fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </TableCell>

        <TableCell
          sx={{
            width: '60px',
            minWidth: '60px',
            textAlign: 'center',
            verticalAlign: 'middle',
            fontWeight: 600,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
            <Typography
              variant="caption"
              color={
                isComplete
                  ? 'success.main'
                  : totalAllocated > 0 && totalAllocated < 100
                    ? 'error'
                    : totalAllocated > 100
                      ? 'error'
                      : 'text.secondary'
              }
              fontWeight={500}
            >
              {totalAllocated.toFixed(0)}%
            </Typography>
            {totalAllocated > 0 && totalAllocated < 100 && (
              <Tooltip title="less than 100% of these activities are allocated" arrow>
                <HelpOutline sx={{ fontSize: 12, color: 'error.main' }} />
              </Tooltip>
            )}
          </Stack>
        </TableCell>

        {sortedActivities.map((activity) => (
          <TableCell
            key={activity.id}
            sx={{ width: '100px', minWidth: '100px', p: 1, textAlign: 'center' }}
          >
            <MemoCustomPercentageInput
              value={allocations[activity.id] || 0}
              onChange={(value) => handleLocalAllocationChange(activity.id, value)}
              disabled={account.isExcluded || isSaving}
              width={80}
            />
          </TableCell>
        ))}
      </TableRow>
    );
  },
  (prev, next) => {
    if (prev.account !== next.account) return false;
    if (prev.saving !== next.saving) return false;
    // fast shallow compare for initialAllocations keys/values
    const aKeys = Object.keys(prev.initialAllocations || {});
    const bKeys = Object.keys(next.initialAllocations || {});
    if (aKeys.length !== bKeys.length) return false;
    for (let k of aKeys) {
      if (
        (prev.initialAllocations as Record<string, number>)[k] !==
        (next.initialAllocations as Record<string, number>)[k]
      )
        return false;
    }
    return true;
  }
);

export default AccountAllocationRow;
